import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useEffect, useRef, useState } from "react";
import { ArrowRight, Check, Lock, ShieldCheck } from "lucide-react";
import {
  MEMBERSHIP_FEATURE_GROUPS,
  MEMBERSHIP_PLANS,
  formatPlanPrice,
  type MembershipPlan,
} from "@/lib/membership-plans";
import { readSelectedPlanId, setSelectedPlanId } from "@/lib/selected-plan";
import { PlanCarousel } from "@/components/membership/PlanCarousel";
import { useMembership } from "@/hooks/useMembership";
import { useIsNativeApp } from "@/lib/native-app";
import { trackFunnel } from "@/lib/funnel";
import { track } from "@/lib/track";
import {
  PRICING_VERSION,
  trackPlanSelected,
  useScrollDepth,
  useSectionInView,
} from "@/lib/pricing-analytics";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import founderImage from "@/assets/founder-jana.jpg";

const DESCRIPTION =
  "Choose your Recovery Trauma™ membership: $14.99 monthly, $59.99 every 6 months, or $89.99 yearly. All plans include the same full access. Cancel anytime.";

export const Route = createFileRoute("/choose-membership")({
  head: () => ({
    meta: [
      { title: "Choose Your Membership — Recovery Trauma™" },
      { name: "description", content: DESCRIPTION },
      { property: "og:title", content: "Choose Your Membership — Recovery Trauma™" },
      { property: "og:description", content: DESCRIPTION },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary_large_image" },
    ],
    links: [{ rel: "canonical", href: "https://recoverytrauma.com/choose-membership" }],
  }),
  component: ChooseMembershipPage,
});

const STEPS = [
  { n: 1, title: "Choose a plan", hint: "Same access on every plan" },
  { n: 2, title: "Create your account", hint: "About 1 minute" },
  { n: 3, title: "Pay securely", hint: "Card or PayPal" },
];

const FAQS: { q: string; a: string }[] = [
  {
    q: "Can I cancel easily?",
    a: "Yes. You can cancel yourself at any time from My Membership in your account — no email or phone call needed. You keep access until the end of the period you have already paid for, and you won't be charged again.",
  },
  {
    q: "Is this therapy?",
    a: "No. Recovery Trauma™ is educational and wellbeing support built from lived experience. It is not a substitute for professional medical or mental-health care, and it can sit alongside therapy if you have it. If you are in crisis, please contact your local emergency services.",
  },
  {
    q: "Is my journal private?",
    a: "Yes. What you write is yours. Your journal is never read or analysed automatically, and optional reflections only happen if you choose to switch them on.",
  },
  {
    q: "What if my payment doesn't go through?",
    a: "No money is taken unless the payment completes. You'll come back to the payment step with both card and PayPal available to try again, and your account stays saved.",
  },
  {
    q: "Can I change plans later?",
    a: "Yes — if you'd like a different plan later, contact us through member support and we'll help you switch.",
  },
];

function ChooseMembershipPage() {
  const navigate = useNavigate();
  const { isAuthed, isMember } = useMembership();
  const nativeApp = useIsNativeApp();

  // Monthly selected by default; honour an earlier choice once hydrated.
  const [selectedId, setSelectedId] = useState<string>(MEMBERSHIP_PLANS[0].id);
  useEffect(() => {
    const prev = readSelectedPlanId();
    if (prev && MEMBERSHIP_PLANS.some((p) => p.id === prev)) setSelectedId(prev);
  }, []);
  const selected = MEMBERSHIP_PLANS.find((p) => p.id === selectedId) ?? MEMBERSHIP_PLANS[0];

  const priceRef = useRef<HTMLDivElement>(null);
  const faqRef = useRef<HTMLElement>(null);
  useSectionInView(priceRef, "price_section_viewed", {}, "choose_membership");
  useSectionInView(faqRef, "faq_viewed", {}, "choose_membership");
  useScrollDepth("choose_membership");

  useEffect(() => {
    trackFunnel(
      "membership_viewed",
      { source: "choose_membership", dedupeKey: "choose_membership_view" },
      { surface: "plan_selection", pricing_version: PRICING_VERSION },
    );
  }, []);

  const select = (plan: MembershipPlan) => {
    setSelectedId(plan.id);
    setSelectedPlanId(plan.id);
    track(
      "plan_select",
      { plan: plan.id, price_usd: plan.priceCents / 100, pricing_version: PRICING_VERSION },
      { source: "choose_membership" },
    );
  };

  const proceed = (where: "main" | "sticky") => {
    const plan = selected;
    setSelectedPlanId(plan.id);
    track(
      "plan_continue",
      {
        plan: plan.id,
        price_usd: plan.priceCents / 100,
        cta: where,
        pricing_version: PRICING_VERSION,
      },
      { source: "choose_membership" },
    );
    trackPlanSelected(plan.id, plan.priceCents / 100, "choose_membership");
    if (isMember) {
      navigate({ to: "/dashboard" });
      return;
    }
    // Carry the plan in the URL too — storage is blocked in some browsers.
    navigate({
      to: isAuthed ? "/complete-membership" : "/signup",
      search: { plan: plan.id },
    });
  };

  const shortLabel =
    selected.interval === "year" ? "Yearly" : selected.intervalCount === 1 ? "Monthly" : "6 months";
  const ctaLabel = `Continue — ${shortLabel} · ${formatPlanPrice(selected)}`;

  // Inside the iOS app: no prices or purchase paths.
  if (nativeApp) {
    return (
      <div className="mx-auto max-w-xl px-4 py-16 text-center">
        <h1 className="font-display text-2xl text-foreground">Recovery Trauma™ Membership</h1>
        <p className="mt-3 text-sm text-muted-foreground">
          Already have an account? Sign in to access your membership.
        </p>
        <Link
          to="/auth"
          className="mt-6 inline-flex min-h-[48px] items-center justify-center rounded-full bg-[image:var(--gradient-gold)] px-6 font-display text-foreground shadow-[var(--shadow-gold)]"
        >
          Sign in
        </Link>
      </div>
    );
  }

  return (
    <div className="relative overflow-hidden bg-gradient-to-b from-background via-background to-[var(--gold-soft)]/25 px-4 pb-10 pt-5 sm:pb-16 sm:pt-14">
      <div className="mx-auto w-full max-w-5xl">
        {/* ── Promise ─────────────────────────────────────────────────── */}
        <header className="mx-auto max-w-2xl text-center">
          <h1 className="font-display text-[26px] leading-tight tracking-tight text-foreground sm:text-4xl">
            Your recovery, at your own pace
          </h1>
          <p className="mx-auto mt-2.5 max-w-xl text-[14.5px] leading-relaxed text-foreground/75 sm:text-base">
            Tools, guided practices and education for Healing Beyond Survival Mode™ —
            built from lived experience. Every plan gives the same full access.
          </p>
        </header>

        {/* ── Three steps ─────────────────────────────────────────────── */}
        <ol
          aria-label="How joining works"
          className="mx-auto mt-3 flex max-w-2xl items-center justify-center gap-1.5 text-center sm:mt-5 sm:grid sm:grid-cols-3 sm:gap-2"
        >
          {STEPS.map((s, i) => (
            <li
              key={s.n}
              aria-current={s.n === 1 ? "step" : undefined}
              className={`flex items-center gap-1 sm:block sm:rounded-2xl sm:border sm:px-2 sm:py-2.5 ${
                s.n === 1 ? "sm:border-gold/60 sm:bg-gold/10" : "sm:border-border sm:bg-card/60"
              }`}
            >
              <span
                className={`flex h-4 w-4 shrink-0 items-center justify-center rounded-full text-[9.5px] font-semibold sm:mx-auto sm:h-6 sm:w-6 sm:text-[12px] ${
                  s.n === 1
                    ? "bg-[image:var(--gradient-gold)] text-foreground"
                    : "border border-border text-muted-foreground"
                }`}
              >
                {s.n}
              </span>
              <span
                className={`whitespace-nowrap text-[11px] leading-none sm:mt-1.5 sm:block sm:text-[12.5px] sm:font-medium sm:leading-tight ${
                  s.n === 1 ? "font-semibold text-foreground" : "text-muted-foreground sm:text-foreground"
                }`}
              >
                <span className="sm:hidden">{["Plan", "Account", "Payment"][i]}</span>
                <span className="hidden sm:inline">{s.title}</span>
              </span>
              <span className="mt-0.5 hidden text-[11px] leading-tight text-muted-foreground sm:block">{s.hint}</span>
              {i < STEPS.length - 1 ? (
                <span aria-hidden className="ms-1 h-px w-4 bg-gold/40 sm:hidden" />
              ) : null}
            </li>
          ))}
        </ol>

        {/* ── Plans ───────────────────────────────────────────────────── */}
        <div ref={priceRef} role="radiogroup" aria-label="Membership plans">
          <PlanCarousel
            onChoose={select}
            selectedId={selectedId}
            source="choose_membership"
            hideNote
            mobileSelectedAction={
              <div className="sm:hidden">
                <button
                  type="button"
                  onClick={() => proceed("main")}
                  className="inline-flex min-h-[52px] w-full items-center justify-center gap-2 rounded-full bg-[image:var(--gradient-gold)] px-4 font-display text-[15px] font-medium text-foreground shadow-[var(--shadow-gold)]"
                >
                  <span className="truncate">{ctaLabel}</span>
                  <ArrowRight className="h-4 w-4 shrink-0 rtl:rotate-180" />
                </button>
                <p className="mt-1.5 text-center text-[11px] leading-tight text-muted-foreground">
                  Cancel anytime · Card or PayPal · No payment until step 3
                </p>
              </div>
            }
          />
        </div>

        {/* ── One clear next step (desktop; phones show it under the selected card) ── */}
        <div className="mx-auto mt-5 hidden max-w-md text-center sm:block">
          <button
            type="button"
            onClick={() => proceed("main")}
            className="group inline-flex min-h-[56px] w-full items-center justify-center gap-2 rounded-full bg-[image:var(--gradient-gold)] px-5 font-display text-[16px] font-medium text-foreground shadow-[var(--shadow-gold)] transition hover:-translate-y-0.5"
          >
            <span className="truncate">{ctaLabel}</span>
            <ArrowRight className="h-4 w-4 shrink-0 rtl:rotate-180" />
          </button>
          <ul className="mt-3 hidden flex-wrap items-center justify-center gap-x-3 gap-y-1 text-[12px] text-muted-foreground sm:flex">
            <li className="inline-flex items-center gap-1">
              <Check className="h-3.5 w-3.5 text-[var(--gold-deep)]" /> Cancel anytime from your account
            </li>
            <li className="inline-flex items-center gap-1">
              <Lock className="h-3.5 w-3.5 text-[var(--gold-deep)]" /> Card or PayPal
            </li>
            <li className="inline-flex items-center gap-1">
              <ShieldCheck className="h-3.5 w-3.5 text-[var(--gold-deep)]" /> No payment until step 3
            </li>
          </ul>
          <p className="mt-2 hidden text-[11.5px] leading-relaxed text-muted-foreground sm:block">
            Billed in USD. Memberships renew automatically until cancelled. Access begins once
            payment is completed.
          </p>
        </div>

        {/* ── What's included: swipe on phones, grid on larger screens ── */}
        <section aria-labelledby="included-heading" className="mt-10">
          <h2
            id="included-heading"
            className="text-center font-display text-[20px] leading-snug text-foreground sm:text-2xl"
          >
            What's included in every plan
          </h2>
          <ul className="-mx-4 mt-4 flex snap-x snap-mandatory gap-3 overflow-x-auto px-4 pb-1 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden sm:mx-0 sm:grid sm:grid-cols-3 sm:overflow-visible sm:px-0">
            {MEMBERSHIP_FEATURE_GROUPS.map((group) => (
              <li
                key={group.title}
                className="w-[80%] shrink-0 snap-start rounded-2xl border border-gold/25 bg-card/80 px-4 py-3.5 sm:w-auto"
              >
                <p className="flex items-center gap-2 font-display text-[15px] text-foreground">
                  <span className="inline-flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-[image:var(--gradient-gold)]">
                    <Check className="h-3 w-3 text-foreground" strokeWidth={3} />
                  </span>
                  {group.title}
                </p>
                <p className="mt-1.5 text-[12.5px] leading-relaxed text-foreground/75">
                  {group.items.join(" · ")}
                </p>
              </li>
            ))}
          </ul>
        </section>

        {/* ── Founder note ────────────────────────────────────────────── */}
        <section className="mx-auto mt-10 flex max-w-2xl items-start gap-4 rounded-[22px] border border-gold/30 bg-card/80 p-4 sm:p-6">
          <img
            src={founderImage}
            alt="Jana, founder of Recovery Trauma™"
            loading="lazy"
            className="h-16 w-16 shrink-0 rounded-full object-cover sm:h-20 sm:w-20"
          />
          <div>
            <p className="text-[14px] leading-relaxed text-foreground/85">
              "I built Recovery Trauma™ from my own experience of living in survival mode. It is a
              place to learn, practise and heal at your own pace — with no pressure, and the choice
              to pause whenever you need."
            </p>
            <p className="mt-2 text-[12.5px] font-medium text-[var(--gold-deep)]">
              Jana, founder
            </p>
          </div>
        </section>

        {/* ── Honest questions ────────────────────────────────────────── */}
        <section ref={faqRef} aria-labelledby="faq-heading" className="mx-auto mt-10 max-w-2xl">
          <h2
            id="faq-heading"
            className="text-center font-display text-[20px] text-foreground sm:text-2xl"
          >
            Questions you might have
          </h2>
          <Accordion type="single" collapsible className="mt-3">
            {FAQS.map((f, i) => (
              <AccordionItem key={f.q} value={`q${i}`}>
                <AccordionTrigger className="min-h-[48px] text-start text-[14.5px]">
                  {f.q}
                </AccordionTrigger>
                <AccordionContent className="text-[13.5px] leading-relaxed text-foreground/80">
                  {f.a}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </section>

        <p className="mt-8 text-center text-sm text-muted-foreground">
          Already have an account?{" "}
          <Link
            to="/auth"
            className="font-medium text-[var(--gold-deep)] underline-offset-4 hover:underline"
          >
            Sign in
          </Link>
        </p>
      </div>

    </div>
  );
}
